import { Marked } from "marked";
import renderResourceChip from "./resource_link_renderer";
import renderHexSwatches from "./color_swatch_renderer";
import linkAttributes from "./link_attributes";
import escapeAttr from "./escape_attr";
import sanitizeMessageHtml from "./sanitize_message_html";
import { iconPlaceholder, upgradeIcons } from "./massimo_icons";
import safeParsedHref from "./safe_href";

const MESSAGE_TYPES = { email: "Email", sms: "SMS" };
const MESSAGE_ICONS = { email: "message-email", sms: "message-sms" };

function detectMessageType(text) {
  const firstLine = text.trim().split("\n")[0].trim();
  for (const [key, label] of Object.entries(MESSAGE_TYPES)) {
    if (firstLine === label) return key;
  }
  return null;
}

function stripTypeLabel(html, label) {
  // Remove label from first <p> — either the whole <p> or just the leading text + <br>
  return html
    .replace(new RegExp(`^\\s*<p>\\s*${label}\\s*</p>`, "i"), "")
    .replace(new RegExp(`^\\s*<p>\\s*${label}\\s*<br>`, "i"), "<p>")
    .trim();
}

const md = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    // Raw HTML in the source is content, not markup.
    html({ text }) {
      return escapeAttr(text);
    },
    blockquote(token) {
      const type = detectMessageType(token.text);
      if (type) {
        const body = this.parser.parse(token.tokens);
        const inner = stripTypeLabel(body, MESSAGE_TYPES[type]);
        return `<div class="my-3">
          <span class="flex items-center gap-1 text-sm font-bold text-gray-800 mb-1">${iconPlaceholder(MESSAGE_ICONS[type])} ${MESSAGE_TYPES[type]}</span>
          <div class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 not-prose text-sm text-gray-800 [&>p]:my-2 [&>:first-child]:!mt-0 [&>:last-child]:!mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4">${inner}</div>
        </div>`;
      }
      return `<blockquote>${this.parser.parse(token.tokens)}</blockquote>`;
    },
    link({ href, title, text }) {
      if (title && title.startsWith("resource:")) {
        const type = title.slice("resource:".length);
        return renderResourceChip(text, href, type);
      }

      const titleAttr = title ? ` title="${escapeAttr(title)}"` : "";
      const url = safeParsedHref(href);
      if (!url) return text;

      const inPlace = url.origin === window.location.origin && url.pathname.startsWith("/admin");

      return `<a href="${escapeAttr(href)}"${titleAttr}${linkAttributes({ inPlace })}>${text}</a>`;
    },
  },
});

// Sanitize before the swatch renderer: it parses HTML into a detached element, which would fetch
// anything remote still in it.
export default function renderMessageMarkdown(element, markdown) {
  element.innerHTML = renderHexSwatches(sanitizeMessageHtml(md.parse(markdown)));
  upgradeIcons(element);
}
